import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import {
  Wrench,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Download,
  ShieldCheck,
  Activity,
  Calendar,
  UserCheck,
  Check,
  Timer,
  ChevronRight,
  Flame,
  Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate, useSearch, useLocation } from '@tanstack/react-router';
import { useHeaderSearch } from '@/hooks/useHeaderSearch';
import { useRowSelection } from '@/hooks/useRowSelection';
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from '@/components/shared/DataTableRow';
import { BulkActionBar } from '@/components/shared/BulkActionBar';
import { exportToExcel, formatDateForFile } from '@/lib/excel.utils';

import { toVnDateString } from "@apv-erp/status";
import { emsTabClass } from "@/components/ui/ems-tabs";
type ActiveTab = 'ACTIVE_TICKETS' | 'PREVENTIVE_PM' | 'HISTORY';

export default function MaintenanceControlPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const isIncidentsMode = location.pathname.startsWith('/mes/incidents');
  const searchParams = useSearch({ strict: false }) as any;
  const initialMachineId = searchParams?.machineId || 'ALL';

  const [activeTab, setActiveTab] = useState<ActiveTab>(
    isIncidentsMode ? 'ACTIVE_TICKETS' : 'PREVENTIVE_PM'
  );
  const [selectedMachineFilter, setSelectedMachineFilter] = useState<string>(initialMachineId);
  // Tìm kiếm dùng ô tìm kiếm chung trên header (?q)
  const { headerSearch: searchQuery } = useHeaderSearch();

  // Đồng bộ activeTab khi chuyển đổi giữa /mes/incidents và /mes/maintenance
  useEffect(() => {
    if (location.pathname.startsWith('/mes/incidents')) {
      setActiveTab('ACTIVE_TICKETS');
    } else if (location.pathname.startsWith('/mes/maintenance')) {
      setActiveTab('PREVENTIVE_PM');
    }
  }, [location.pathname]);

  // Modal Báo Cáo Sự Cố Mới (Khẩn cấp cho sàn xưởng)
  const [reportModal, setReportModal] = useState<{
    isOpen: boolean;
    machineId: string;
    issueType: string;
    reason: string;
    severity: 'CRITICAL' | 'WARNING';
    notes: string;
  }>({
    isOpen: false,
    machineId: '',
    issueType: 'Cơ khí',
    reason: '',
    severity: 'CRITICAL',
    notes: '',
  });

  // Modals state
  const [assignModal, setAssignModal] = useState<{
    isOpen: boolean;
    ticket: any | null;
    technicianName: string;
  }>({
    isOpen: false,
    ticket: null,
    technicianName: '',
  });

  const [resolveModal, setResolveModal] = useState<{
    isOpen: boolean;
    ticket: any | null;
    fixAction: string;
    durationMinutes: number;
    partsReplaced: string;
    newMachineStatus: 'IDLE' | 'RUNNING';
  }>({
    isOpen: false,
    ticket: null,
    fixAction: '',
    durationMinutes: 30,
    partsReplaced: '',
    newMachineStatus: 'IDLE',
  });

  const [scheduleModal, setScheduleModal] = useState<{
    isOpen: boolean;
    machine: any | null;
    scheduledDate: string;
    cycleMonths: number;
    notes: string;
  }>({
    isOpen: false,
    machine: null,
    scheduledDate: toVnDateString(Date.now() + 30 * 86400000),
    cycleMonths: 3,
    notes: '',
  });

  // Query dữ liệu TPM từ backend (100% dữ liệu thực từ DB)
  const { data, isLoading } = useQuery({
    queryKey: ['maintenance-dashboard', selectedMachineFilter],
    queryFn: async () => {
      return await trpcClient.maintenance.getMaintenanceDashboard.query({
        machineId: selectedMachineFilter,
      });
    },
    refetchInterval: 15_000,
  });

  // Mutations
  const assignTechnicianMutation = useMutation({
    mutationFn: async (args: { ticketId: string; technicianName?: string }) => {
      return await trpcClient.maintenance.assignTechnician.mutate(args);
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setAssignModal({ isOpen: false, ticket: null, technicianName: '' });
      queryClient.invalidateQueries({ queryKey: ['maintenance-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Lỗi khi tiếp nhận phiếu');
    },
  });

  const resolveTicketMutation = useMutation({
    mutationFn: async (args: {
      ticketId: string;
      fixAction: string;
      durationMinutes: number;
      partsReplaced?: string;
      newMachineStatus?: 'IDLE' | 'RUNNING';
    }) => {
      return await trpcClient.maintenance.resolveMaintenanceTicket.mutate(args);
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setResolveModal({
        isOpen: false,
        ticket: null,
        fixAction: '',
        durationMinutes: 30,
        partsReplaced: '',
        newMachineStatus: 'IDLE',
      });
      queryClient.invalidateQueries({ queryKey: ['maintenance-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Lỗi khi nghiệm thu');
    },
  });

  const schedulePmMutation = useMutation({
    mutationFn: async (args: {
      machineId: string;
      scheduledDate: string;
      cycleMonths?: number;
      notes?: string;
    }) => {
      return await trpcClient.maintenance.schedulePreventiveMaintenance.mutate(args);
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setScheduleModal({
        isOpen: false,
        machine: null,
        scheduledDate: toVnDateString(Date.now() + 30 * 86400000),
        cycleMonths: 3,
        notes: '',
      });
      queryClient.invalidateQueries({ queryKey: ['maintenance-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Lỗi khi lập lịch bảo trì');
    },
  });

  const reportIncidentMutation = useMutation({
    mutationFn: async (args: {
      machineId: string;
      issueType: string;
      reason: string;
      severity: 'CRITICAL' | 'WARNING';
      notes?: string;
    }) => {
      return await trpcClient.maintenance.reportEmergencyIncident.mutate(args);
    },
    onSuccess: (res) => {
      toast.success(res.message);
      setReportModal({
        isOpen: false,
        machineId: '',
        issueType: 'Cơ khí',
        reason: '',
        severity: 'CRITICAL',
        notes: '',
      });
      queryClient.invalidateQueries({ queryKey: ['maintenance-dashboard'] });
    },
    onError: (err: any) => {
      toast.error(err.message || 'Lỗi khi kích hoạt báo sự cố');
    },
  });

  const kpi = data?.kpi || {
    totalMachines: 0,
    readyCount: 0,
    runningCount: 0,
    idleCount: 0,
    downCount: 0,
    maintenanceCount: 0,
    availabilityRate: 100,
    activeTicketsCount: 0,
    resolvedTicketsCount: 0,
    mttrMinutes: 0,
    mtbfHours: 0,
    totalDowntimeHours: 0,
  };

  const machines = data?.machines || [];
  const activeTickets = data?.activeTickets || [];
  const recentHistory = data?.recentHistory || [];

  // Lọc tickets theo search
  const filteredActiveTickets = useMemo(() => {
    if (!searchQuery.trim()) return activeTickets;
    const q = searchQuery.toLowerCase();
    return activeTickets.filter(
      (t: any) =>
        t.machineName?.toLowerCase().includes(q) ||
        t.machineCode?.toLowerCase().includes(q) ||
        t.logId?.toLowerCase().includes(q) ||
        t.issueType?.toLowerCase().includes(q) ||
        t.reason?.toLowerCase().includes(q)
    );
  }, [activeTickets, searchQuery]);

  const filteredMachines = useMemo(() => {
    if (!searchQuery.trim()) return machines;
    const q = searchQuery.toLowerCase();
    return machines.filter(
      (m: any) =>
        m.name?.toLowerCase().includes(q) ||
        m.machineId?.toLowerCase().includes(q) ||
        m.departmentName?.toLowerCase().includes(q)
    );
  }, [machines, searchQuery]);

  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) return recentHistory;
    const q = searchQuery.toLowerCase();
    return recentHistory.filter(
      (h: any) =>
        h.machineName?.toLowerCase().includes(q) ||
        h.machineCode?.toLowerCase().includes(q) ||
        h.logId?.toLowerCase().includes(q) ||
        h.issueType?.toLowerCase().includes(q) ||
        h.reason?.toLowerCase().includes(q)
    );
  }, [recentHistory, searchQuery]);

  // ── Chọn nhiều dòng theo tab đang mở ──
  const tabRows: any[] =
    activeTab === 'ACTIVE_TICKETS' ? filteredActiveTickets : activeTab === 'PREVENTIVE_PM' ? filteredMachines : filteredHistory;
  const rowIds = useMemo(() => tabRows.map((r: any) => r.id), [tabRows]);
  const rowSelection = useRowSelection(rowIds);
  useEffect(() => {
    rowSelection.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openTicket = (row: any) => navigate({ to: '/machine-logs/$logId', params: { logId: row.id } });
  const openMachine = (m: any) => navigate({ to: '/machines/$machineId', params: { machineId: m.id } });

  const fmtDateTime = (v: any) => (v ? new Date(v).toLocaleString('vi-VN') : '');
  const fmtDate = (v: any) => (v ? new Date(v).toLocaleDateString('vi-VN') : '');
  const exportRows = async (rows: any[], suffix = '') => {
    if (activeTab === 'PREVENTIVE_PM') {
      await exportToExcel(
        rows,
        [
          { key: 'machineId', label: 'Mã thiết bị', width: 14 },
          { key: 'name', label: 'Tên máy', width: 28 },
          { key: 'departmentName', label: 'Khu vực', width: 20 },
          { key: 'status', label: 'Trạng thái', width: 14 },
          { key: 'totalRunHours', label: 'Tổng giờ chạy', width: 14, type: 'number' },
          { key: 'cycleHours', label: 'Chu kỳ PM (giờ)', width: 14, type: 'number' },
          { key: 'hoursSinceLastMaintenance', label: 'Giờ từ lần PM trước', width: 18, type: 'number' },
          { key: 'dueProgressPercent', label: 'Tiến độ đến hạn (%)', width: 16, type: 'number' },
          { key: 'maintenanceNext', label: 'Hạn kế tiếp', width: 14, format: (v: any) => fmtDate(v) },
        ],
        `Bao-tri-dinh-ky${suffix}_${formatDateForFile()}`,
        { sheetName: 'Định kỳ PM' }
      );
      return;
    }
    const isHistory = activeTab === 'HISTORY';
    await exportToExcel(
      rows,
      [
        { key: 'logId', label: 'Mã phiếu', width: 16 },
        { key: 'machineName', label: 'Thiết bị', width: 24 },
        { key: 'machineCode', label: 'Mã thiết bị', width: 14 },
        { key: 'issueType', label: 'Loại sự cố', width: 18 },
        { key: 'reason', label: 'Hiện tượng / Lý do', width: 32 },
        ...(isHistory
          ? [
              { key: 'fixAction', label: 'Phương án khắc phục', width: 32 },
              { key: 'durationMinutes', label: 'Thời gian sửa (phút)', width: 16, type: 'number' as const },
            ]
          : [{ key: 'status', label: 'Trạng thái', width: 14, format: (v: any) => (v === 'OPEN' ? 'Chờ tiếp nhận' : 'Đang sửa chữa') }]),
        { key: 'date', label: 'Thời điểm', width: 18, format: (v: any) => fmtDateTime(v) },
        { key: 'operatorName', label: isHistory ? 'Kỹ thuật viên' : 'Người báo', width: 20 },
      ],
      `${isHistory ? 'Lich-su-sua-chua' : 'Su-co-dang-xu-ly'}${suffix}_${formatDateForFile()}`,
      { sheetName: isHistory ? 'Lịch sử sửa chữa' : 'Sự cố đang xử lý' }
    );
  };
  const handleExport = async () => {
    try {
      await exportRows(tabRows);
    } catch (e: any) {
      toast.error(e?.message || 'Không xuất được Excel');
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/70 text-slate-900 p-4 md:p-6 flex flex-col font-sans gap-4">
      {/* ── 1. Thanh thao tác: tab (trái) + nút (phải, nút tạo ở cuối). Tiêu đề đã có trên header chung ── */}
      <div className="erp-list-toolbar-row bg-white border border-slate-200 rounded-xl px-2 py-1.5 shadow-sm flex flex-wrap items-center justify-between gap-2">
        <div role="tablist" aria-label="Nhóm dữ liệu bảo trì" className="erp-list-primary flex items-center gap-1.5 overflow-x-auto hide-scrollbar flex-nowrap">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'ACTIVE_TICKETS'}
            onClick={() => setActiveTab('ACTIVE_TICKETS')}
            className={emsTabClass(activeTab === 'ACTIVE_TICKETS', { size: "sm" })}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>Sự cố đang xử lý</span>
            {kpi.activeTicketsCount > 0 && (
              <span className="px-1.5 py-0.2 rounded-full bg-rose-500 text-white text-[10px] font-black">
                {kpi.activeTicketsCount}
              </span>
            )}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'PREVENTIVE_PM'}
            onClick={() => setActiveTab('PREVENTIVE_PM')}
            className={emsTabClass(activeTab === 'PREVENTIVE_PM', { size: "sm" })}
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Giờ chạy &amp; PM</span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'HISTORY'}
            onClick={() => setActiveTab('HISTORY')}
            className={emsTabClass(activeTab === 'HISTORY', { size: "sm" })}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Lịch sử sửa chữa ({kpi.resolvedTicketsCount})</span>
          </button>
        </div>

        <div className="erp-list-actions flex items-center gap-1.5 ml-auto max-w-full min-w-0 overflow-x-auto hide-scrollbar flex-nowrap [&>*]:shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate({ to: '/mes/andon' })}
            className="bg-white border-blue-300 hover:bg-blue-50 text-blue-700 font-semibold text-xs h-7"
          >
            Andon xưởng
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            className="bg-white text-xs h-7 gap-1"
            title="Xuất Excel bảng đang xem"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Xuất Excel</span>
          </Button>

          <Button
            size="sm"
            onClick={() =>
              setReportModal((prev) => ({
                ...prev,
                isOpen: true,
                machineId: prev.machineId || (machines.length > 0 ? machines[0].id : ''),
              }))
            }
            className="bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs h-7 flex items-center gap-1.5"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Báo sự cố
          </Button>

          {!isIncidentsMode && (
            <Button
              size="sm"
              onClick={() => {
                if (machines.length > 0) {
                  setScheduleModal({
                    isOpen: true,
                    machine: machines[0],
                    scheduledDate: toVnDateString(Date.now() + 30 * 86400000),
                    cycleMonths: 3,
                    notes: '',
                  });
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs h-7 flex items-center gap-1.5"
            >
              <Calendar className="w-3.5 h-3.5" />
              Lập lịch PM
            </Button>
          )}
        </div>
      </div>

      {/* ── 2. KPI Ribbon 6 Khối Sức Khỏe Thiết Bị ── */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Tổng số máy */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Tổng Số Máy</div>
          <div className="text-2xl font-black text-slate-900 mt-1 flex items-baseline gap-1">
            {kpi.totalMachines}
            <span className="text-xs font-normal text-slate-500">thiết bị</span>
          </div>
        </div>

        {/* Tỷ lệ sẵn sàng */}
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-bold flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Tỷ Lệ Sẵn Sàng
          </div>
          <div className="text-2xl font-black text-emerald-700 mt-1 flex items-baseline gap-1">
            {kpi.availabilityRate}%
            <span className="text-xs font-normal text-emerald-600">vận hành</span>
          </div>
        </div>

        {/* Máy đang dừng sự cố */}
        <div className={`rounded-xl p-3 shadow-sm border-2 ${
          kpi.downCount > 0 ? 'bg-rose-50 border-rose-400 animate-pulse' : 'bg-white border-slate-200'
        }`}>
          <div className={`text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 ${
            kpi.downCount > 0 ? 'text-rose-800' : 'text-slate-500'
          }`}>
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" /> Máy Sự Cố (DOWN)
          </div>
          <div className={`text-2xl font-black mt-1 flex items-baseline gap-1 ${
            kpi.downCount > 0 ? 'text-rose-700' : 'text-slate-900'
          }`}>
            {kpi.downCount}
            <span className="text-xs font-normal text-slate-500">máy</span>
          </div>
        </div>

        {/* Máy đang bảo trì */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-amber-800 font-bold flex items-center gap-1.5">
            <Wrench className="w-3.5 h-3.5 text-amber-600" /> Đang Bảo Trì
          </div>
          <div className="text-2xl font-black text-amber-700 mt-1 flex items-baseline gap-1">
            {kpi.maintenanceCount}
            <span className="text-xs font-normal text-amber-600">máy</span>
          </div>
        </div>

        {/* MTTR Thực Tế */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
            <Timer className="w-3.5 h-3.5 text-slate-600" /> MTTR Sửa Chữa
          </div>
          <div className="text-2xl font-black text-blue-700 mt-1 flex items-baseline gap-1">
            {kpi.mttrMinutes}
            <span className="text-xs font-normal text-slate-500">phút/lần</span>
          </div>
        </div>

        {/* MTBF Thực Tế */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-600" /> MTBF Giữa Các Sự Cố
          </div>
          <div className="text-2xl font-black text-indigo-700 mt-1 flex items-baseline gap-1">
            {kpi.mtbfHours}
            <span className="text-xs font-normal text-slate-500">giờ</span>
          </div>
        </div>
      </section>

      {/* ── 4. Main Content Area ── */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200 text-slate-500">
          <RefreshCw className="w-8 h-8 animate-spin text-amber-600 mb-2" />
          <p className="text-xs font-bold">Đang tải dữ liệu điều hành bảo trì...</p>
        </div>
      ) : activeTab === 'ACTIVE_TICKETS' ? (
        /* ── TAB 1: DANH SÁCH PHIẾU SỰ CỐ ĐANG MỞ / ĐANG XỬ LÝ ── */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden md:overflow-visible erp-table-wrapper">
          {filteredActiveTickets.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2 opacity-80" />
              <p className="text-sm font-bold text-slate-800">Không có sự cố nào đang cần xử lý</p>
              <p className="text-xs text-slate-500 mt-1">Toàn bộ thiết bị trong xưởng đang ở trạng thái an toàn.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200 uppercase text-[11px]">
                  <tr>
                    <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                    <th className="p-3">Mã Phiếu</th>
                    <th className="p-3">Thiết Bị</th>
                    <th className="p-3">Loại Sự Cố</th>
                    <th className="p-3">Hiện Tượng / Lý Do</th>
                    <th className="p-3">Thời Điểm Báo</th>
                    <th className="p-3">Trạng Thái</th>
                    <th className="p-3">Người Báo / Phụ Trách</th>
                    <th className="p-3 text-right">Thao Tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredActiveTickets.map((ticket: any) => {
                    const isOpen = ticket.status === 'OPEN';
                    return (
                      <DataTableRow
                        key={ticket.id}
                        id={ticket.id}
                        rowSelection={rowSelection}
                        onClick={() => openTicket(ticket)}
                        className="hover:bg-slate-50/80 transition-colors"
                      >
                        <DataTableRowCheckbox id={ticket.id} rowSelection={rowSelection} />
                        <td className="p-3 font-mono font-bold text-blue-700">
                          {ticket.logId}
                        </td>
                        <td className="p-3">
                          <div className="font-bold text-slate-900">{ticket.machineName}</div>
                          <div className="font-mono text-[11px] text-slate-500">{ticket.machineCode}</div>
                        </td>
                        <td className="p-3 font-bold text-slate-700">
                          {ticket.issueType}
                        </td>
                        <td className="p-3 max-w-xs">
                          <div className="font-medium text-slate-900 truncate" title={ticket.reason}>
                            {ticket.reason}
                          </div>
                          {ticket.parameters?.notes && (
                            <div className="text-[11px] text-slate-500 truncate">{ticket.parameters.notes}</div>
                          )}
                        </td>
                        <td className="p-3 text-slate-600 font-mono">
                          {new Date(ticket.date).toLocaleString('vi-VN')}
                        </td>
                        <td className="p-3">
                          {isOpen ? (
                            <Badge className="bg-rose-600 text-white font-black text-[10px] animate-pulse">
                              CHỜ TIẾP NHẬN
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500 text-slate-950 font-bold text-[10px]">
                              ĐANG SỬA CHỮA
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="text-slate-800 font-medium">{ticket.operatorName}</div>
                          {ticket.parameters?.technicianName && (
                            <div className="text-[11px] text-indigo-700 font-bold">
                              KTV: {ticket.parameters.technicianName}
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="inline-flex items-center gap-1.5">
                            {isOpen ? (
                              <Button
                                size="sm"
                                onClick={() =>
                                  setAssignModal({
                                    isOpen: true,
                                    ticket,
                                    technicianName: '',
                                  })
                                }
                                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold h-7 px-2.5"
                              >
                                Tiếp Nhận
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() =>
                                  setResolveModal({
                                    isOpen: true,
                                    ticket,
                                    fixAction: '',
                                    durationMinutes: 20,
                                    partsReplaced: '',
                                    newMachineStatus: 'IDLE',
                                  })
                                }
                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold h-7 px-2.5"
                              >
                                Nghiệm Thu &amp; Đóng
                              </Button>
                            )}
                          </div>
                        </td>
                      </DataTableRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === 'PREVENTIVE_PM' ? (
        /* ── TAB 2: GIÁM SÁT GIỜ CHẠY MÁY & ĐỊNH KỲ PM ── */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden md:overflow-visible erp-table-wrapper">
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200 uppercase text-[11px]">
                <tr>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <th className="p-3">Mã Thiết Bị</th>
                  <th className="p-3">Tên Máy &amp; Khu Vực</th>
                  <th className="p-3">Trạng Thái</th>
                  <th className="p-3 text-right">Tổng Giờ Chạy Lũy Kế</th>
                  <th className="p-3 text-right">Chu Kỳ PM</th>
                  <th className="p-3">Tiến Độ Đến Hạn PM</th>
                  <th className="p-3">Hạn Kế Tiếp</th>
                  <th className="p-3 text-right">Thao Tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredMachines.map((m: any) => {
                  return (
                    <DataTableRow
                      key={m.id}
                      id={m.id}
                      rowSelection={rowSelection}
                      onClick={() => openMachine(m)}
                      className="hover:bg-slate-50/80 transition-colors"
                    >
                      <DataTableRowCheckbox id={m.id} rowSelection={rowSelection} />
                      <td className="p-3 font-mono font-bold text-blue-700">{m.machineId}</td>
                      <td className="p-3">
                        <div className="font-bold text-slate-900">{m.name}</div>
                        <div className="text-[11px] text-slate-500">{m.departmentName}</div>
                      </td>
                      <td className="p-3">
                        {m.status === 'RUNNING' ? (
                          <Badge className="bg-emerald-600 text-white font-bold text-[10px]">ĐANG CHẠY</Badge>
                        ) : m.status === 'DOWN' ? (
                          <Badge className="bg-rose-600 text-white font-bold text-[10px]">SỰ CỐ</Badge>
                        ) : m.status === 'MAINTENANCE' ? (
                          <Badge className="bg-amber-500 text-slate-950 font-bold text-[10px]">BẢO TRÌ</Badge>
                        ) : (
                          <Badge className="bg-slate-200 text-slate-700 font-bold text-[10px]">CHỜ VIỆC</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right font-mono font-bold text-slate-900">
                        {m.totalRunHours} giờ
                      </td>
                      <td className="p-3 text-right font-mono text-slate-600">
                        {m.cycleHours} giờ
                      </td>
                      <td className="p-3 min-w-[140px]">
                        <div className="flex items-center justify-between text-[11px] mb-1 font-bold">
                          <span className={m.isOverdue ? 'text-rose-600' : 'text-slate-700'}>
                            {m.hoursSinceLastMaintenance} / {m.cycleHours}h
                          </span>
                          <span className={m.isOverdue ? 'text-rose-600' : 'text-slate-600'}>
                            {m.dueProgressPercent}%
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              m.dueProgressPercent >= 90
                                ? 'bg-rose-600'
                                : m.dueProgressPercent >= 70
                                ? 'bg-amber-500'
                                : 'bg-emerald-500'
                            }`}
                            style={{ width: `${m.dueProgressPercent}%` }}
                          />
                        </div>
                      </td>
                      <td className="p-3 font-mono text-slate-600">
                        {m.maintenanceNext ? (
                          new Date(m.maintenanceNext).toLocaleDateString('vi-VN')
                        ) : (
                          <span className="text-slate-400">Chưa đặt lịch</span>
                        )}
                      </td>
                      <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setScheduleModal({
                              isOpen: true,
                              machine: m,
                              scheduledDate: toVnDateString(Date.now() + 30 * 86400000),
                              cycleMonths: 3,
                              notes: '',
                            })
                          }
                          className="h-7 text-xs font-bold border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          Lập phiếu PM
                        </Button>
                      </td>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* ── TAB 3: LỊCH SỬ BẢO TRÌ & SỬA CHỮA HOÀN TẤT ── */
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden md:overflow-visible erp-table-wrapper">
          {filteredHistory.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-2 opacity-80" />
              <p className="text-sm font-bold text-slate-800">Chưa có nhật ký bảo trì nào đã hoàn thành</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200 uppercase text-[11px]">
                  <tr>
                    <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                    <th className="p-3">Mã Phiếu</th>
                    <th className="p-3">Thiết Bị</th>
                    <th className="p-3">Loại</th>
                    <th className="p-3">Nguyên Nhân</th>
                    <th className="p-3">Phương Án Khắc Phục</th>
                    <th className="p-3 text-right">Thời Gian Sửa</th>
                    <th className="p-3">Thời Điểm</th>
                    <th className="p-3">Kỹ Thuật Viên</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredHistory.map((h: any) => (
                    <DataTableRow
                      key={h.id}
                      id={h.id}
                      rowSelection={rowSelection}
                      onClick={() => openTicket(h)}
                      className="hover:bg-slate-50/80 transition-colors"
                    >
                      <DataTableRowCheckbox id={h.id} rowSelection={rowSelection} />
                      <td className="p-3 font-mono font-bold text-slate-700">{h.logId}</td>
                      <td className="p-3 font-bold text-slate-900">{h.machineName}</td>
                      <td className="p-3 text-slate-700 font-medium">{h.issueType}</td>
                      <td className="p-3 text-slate-800 max-w-xs truncate">{h.reason}</td>
                      <td className="p-3 text-emerald-800 font-medium max-w-xs truncate">{h.fixAction}</td>
                      <td className="p-3 text-right font-mono font-bold text-slate-900">
                        {h.durationMinutes} phút
                      </td>
                      <td className="p-3 text-slate-600 font-mono">
                        {new Date(h.date).toLocaleDateString('vi-VN')}
                      </td>
                      <td className="p-3 text-indigo-700 font-bold">{h.operatorName}</td>
                    </DataTableRow>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <BulkActionBar
        selectedCount={rowSelection.selectedCount}
        onClearSelection={rowSelection.clearSelection}
        actions={[
          {
            label: 'Xuất Excel',
            icon: <Download className="w-3.5 h-3.5" />,
            onClick: async () => {
              try {
                await exportRows(tabRows.filter((r: any) => rowSelection.selectedIds.has(r.id)), '_chon');
              } catch (e: any) {
                toast.error(e?.message || 'Không xuất được Excel');
              }
            },
          },
        ]}
      />

      {/* ── MODAL 1: TIẾP NHẬN XỬ LÝ SỰ CỐ ── */}
      <Dialog
        open={assignModal.isOpen}
        onOpenChange={(open) => setAssignModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md bg-white border-amber-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Tiếp Nhận Sửa Chữa &amp; Phân Công KTV
            </DialogTitle>
            <DialogDescription className="text-xs">
              Xác nhận tiếp nhận xử lý sự cố. Máy sẽ chuyển sang trạng thái Đang Bảo Trì.
            </DialogDescription>
          </DialogHeader>

          {assignModal.ticket && (
            <div className="py-2 space-y-3 text-xs">
              <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 flex justify-between items-center">
                <div>
                  <div className="font-bold text-slate-900">{assignModal.ticket.machineName}</div>
                  <div className="text-slate-600 font-mono">{assignModal.ticket.logId} &bull; {assignModal.ticket.issueType}</div>
                </div>
                <Badge className="bg-amber-500 text-slate-950 font-bold text-[10px]">
                  BẢO TRÌ
                </Badge>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Tên Kỹ Thuật Viên Phụ Trách:</label>
                <Input
                  value={assignModal.technicianName}
                  onChange={(e) =>
                    setAssignModal((prev) => ({ ...prev, technicianName: e.target.value }))
                  }
                  placeholder="Nhập tên kỹ thuật viên cơ điện..."
                  className="text-xs h-9 border-slate-300"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAssignModal({ isOpen: false, ticket: null, technicianName: '' })}
              className="text-xs"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={assignTechnicianMutation.isPending}
              onClick={() => {
                if (assignModal.ticket) {
                  assignTechnicianMutation.mutate({
                    ticketId: assignModal.ticket.id,
                    technicianName: assignModal.technicianName,
                  });
                }
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold"
            >
              {assignTechnicianMutation.isPending ? 'Đang xử lý...' : 'Xác Nhận Tiếp Nhận'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── MODAL 2: NGHIỆM THU HOÀN THÀNH & ĐÓNG PHIẾU ── */}
      <Dialog
        open={resolveModal.isOpen}
        onOpenChange={(open) => setResolveModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md bg-white border-emerald-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Nghiệm Thu Hoàn Thành &amp; Khôi Phục Máy
            </DialogTitle>
            <DialogDescription className="text-xs">
              Nhập phương án sửa chữa và thời gian khắc phục để khôi phục máy về trạng thái sản xuất.
            </DialogDescription>
          </DialogHeader>

          {resolveModal.ticket && (
            <div className="py-2 space-y-3 text-xs">
              <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 flex justify-between items-center">
                <div>
                  <div className="font-bold text-slate-900">{resolveModal.ticket.machineName}</div>
                  <div className="text-slate-600 font-mono">{resolveModal.ticket.logId} &bull; {resolveModal.ticket.reason}</div>
                </div>
                <Badge className="bg-emerald-600 text-white font-bold text-[10px]">
                  KHÔI PHỤC
                </Badge>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Phương Án Sửa Chữa / Khắc Phục (*):</label>
                <Input
                  value={resolveModal.fixAction}
                  onChange={(e) =>
                    setResolveModal((prev) => ({ ...prev, fixAction: e.target.value }))
                  }
                  placeholder="Ví dụ: Thay dây đai biến tần, căn chỉnh cử gá..."
                  className="text-xs h-9 border-slate-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Thời Gian Sửa (Phút) (*):</label>
                  <Input
                    type="number"
                    min={1}
                    value={resolveModal.durationMinutes}
                    onChange={(e) =>
                      setResolveModal((prev) => ({
                        ...prev,
                        durationMinutes: Number(e.target.value) || 0,
                      }))
                    }
                    className="text-xs h-9 border-slate-300"
                  />
                </div>
                <div>
                  <label className="font-bold text-slate-700 block mb-1">Trạng Thái Sau Khôi Phục:</label>
                  <select
                    value={resolveModal.newMachineStatus}
                    onChange={(e) =>
                      setResolveModal((prev) => ({
                        ...prev,
                        newMachineStatus: e.target.value as any,
                      }))
                    }
                    className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-xs text-slate-900 font-bold"
                  >
                    <option value="IDLE">CHỜ VIỆC (IDLE)</option>
                    <option value="RUNNING">CHẠY TIẾP (RUNNING)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Phụ Tùng Đã Thay Thế (Nếu có):</label>
                <Input
                  value={resolveModal.partsReplaced}
                  onChange={(e) =>
                    setResolveModal((prev) => ({ ...prev, partsReplaced: e.target.value }))
                  }
                  placeholder="Ví dụ: Vòng bi NSK 6205, Dây curoa 5M-450..."
                  className="text-xs h-9 border-slate-300"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setResolveModal({
                  isOpen: false,
                  ticket: null,
                  fixAction: '',
                  durationMinutes: 30,
                  partsReplaced: '',
                  newMachineStatus: 'IDLE',
                })
              }
              className="text-xs"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!resolveModal.fixAction || resolveTicketMutation.isPending}
              onClick={() => {
                if (resolveModal.ticket && resolveModal.fixAction) {
                  resolveTicketMutation.mutate({
                    ticketId: resolveModal.ticket.id,
                    fixAction: resolveModal.fixAction,
                    durationMinutes: resolveModal.durationMinutes,
                    partsReplaced: resolveModal.partsReplaced,
                    newMachineStatus: resolveModal.newMachineStatus,
                  });
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold"
            >
              {resolveTicketMutation.isPending ? 'Đang lưu...' : 'Nghiệm Thu & Khôi Phục Máy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── MODAL 3: LẬP LỊCH BẢO TRÌ ĐỊNH KỲ PM ── */}
      <Dialog
        open={scheduleModal.isOpen}
        onOpenChange={(open) => setScheduleModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-md bg-white border-blue-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Lập Kế Hoạch Bảo Trì Định Kỳ (Preventive PM)
            </DialogTitle>
            <DialogDescription className="text-xs">
              Lập lịch bảo dưỡng phòng ngừa cho thiết bị theo chu kỳ hoạt động.
            </DialogDescription>
          </DialogHeader>

          {scheduleModal.machine && (
            <div className="py-2 space-y-3 text-xs">
              <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-200">
                <div className="font-bold text-slate-900">{scheduleModal.machine.name}</div>
                <div className="text-slate-600 font-mono">
                  {scheduleModal.machine.machineId} &bull; Đã chạy: {scheduleModal.machine.totalRunHours} giờ
                </div>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Ngày Bảo Trì Kế Tiếp (*):</label>
                <Input
                  type="date"
                  value={scheduleModal.scheduledDate}
                  onChange={(e) =>
                    setScheduleModal((prev) => ({ ...prev, scheduledDate: e.target.value }))
                  }
                  className="text-xs h-9 border-slate-300"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Chu Kỳ Bảo Dưỡng (Tháng):</label>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  value={scheduleModal.cycleMonths}
                  onChange={(e) =>
                    setScheduleModal((prev) => ({
                      ...prev,
                      cycleMonths: Number(e.target.value) || 3,
                    }))
                  }
                  className="text-xs h-9 border-slate-300"
                />
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">Ghi Chú Nội Dung Bảo Dưỡng:</label>
                <Textarea
                  value={scheduleModal.notes}
                  onChange={(e) =>
                    setScheduleModal((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Nội dung kiểm tra: Thay dầu bôi trơn, kiểm tra khe hở vòng bi, vệ sinh tủ điện..."
                  className="text-xs border-slate-300"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setScheduleModal({
                  isOpen: false,
                  machine: null,
                  scheduledDate: toVnDateString(Date.now() + 30 * 86400000),
                  cycleMonths: 3,
                  notes: '',
                })
              }
              className="text-xs"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={schedulePmMutation.isPending}
              onClick={() => {
                if (scheduleModal.machine) {
                  schedulePmMutation.mutate({
                    machineId: scheduleModal.machine.id,
                    scheduledDate: scheduleModal.scheduledDate,
                    cycleMonths: scheduleModal.cycleMonths,
                    notes: scheduleModal.notes,
                  });
                }
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold"
            >
              {schedulePmMutation.isPending ? 'Đang lưu...' : 'Lập Lịch Bảo Trì'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── MODAL 4: BÁO CÁO SỰ CỐ THIẾT BỊ MỚI (KHẨN CẤP SÀN XƯỞNG) ── */}
      <Dialog
        open={reportModal.isOpen}
        onOpenChange={(open) => setReportModal((prev) => ({ ...prev, isOpen: open }))}
      >
        <DialogContent className="max-w-lg bg-white border-rose-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-rose-600" />
              Báo Cáo Sự Cố Thiết Bị Sàn Xưởng
            </DialogTitle>
            <DialogDescription className="text-xs">
              Khai báo dừng máy hoặc sự cố kỹ thuật. Thiết bị sẽ lập tức chuyển sang trạng thái dừng máy (DOWN) để kỹ thuật viên cơ điện tiếp nhận xử lý.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3.5 text-xs">
            <div>
              <label className="font-bold text-slate-700 block mb-1">
                Chọn Máy Bị Sự Cố (*):
              </label>
              <select
                value={reportModal.machineId}
                onChange={(e) =>
                  setReportModal((prev) => ({ ...prev, machineId: e.target.value }))
                }
                className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-rose-500 font-medium text-slate-900"
              >
                <option value="">-- Chọn thiết bị gặp sự cố --</option>
                {machines.map((m: any) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.machineId}) &bull; Trạng thái: {m.status}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  Nhóm Sự Cố (*):
                </label>
                <select
                  value={reportModal.issueType}
                  onChange={(e) =>
                    setReportModal((prev) => ({ ...prev, issueType: e.target.value }))
                  }
                  className="w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-rose-500 font-medium text-slate-900"
                >
                  <option value="Cơ khí">Cơ khí (Kẹt giấy, gãy nhíp, lệch trục...)</option>
                  <option value="Điện / Tủ điện">Điện / Điều khiển (Biến tần, PLC, mất nguồn...)</option>
                  <option value="Khí nén / Bơm">Khí nén / Bơm hút / Áp lực chân không</option>
                  <option value="Mực / Nước / Lô cao su">Mực in / Hệ thống lô / Nhiệt độ sấy</option>
                  <option value="Khuôn / Dao / Tấm lót">Khuôn bế / Dao cắt đứt / Đệm cao su</option>
                  <option value="Khác">Sự cố khác</option>
                </select>
              </div>

              <div>
                <label className="font-bold text-slate-700 block mb-1">
                  Mức Độ Nghiêm Trọng (*):
                </label>
                <select
                  value={reportModal.severity}
                  onChange={(e) =>
                    setReportModal((prev) => ({
                      ...prev,
                      severity: e.target.value as 'CRITICAL' | 'WARNING',
                    }))
                  }
                  className={`w-full h-9 rounded-md border px-3 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-rose-500 ${
                    reportModal.severity === 'CRITICAL'
                      ? 'border-rose-400 bg-rose-50 text-rose-800'
                      : 'border-amber-400 bg-amber-50 text-amber-800'
                  }`}
                >
                  <option value="CRITICAL">🔴 DỪNG MÁY KHẨN CẤP (DOWN)</option>
                  <option value="WARNING">🟡 Cảnh Báo Vận Hành (Máy vẫn chạy)</option>
                </select>
              </div>
            </div>

            <div>
              <label className="font-bold text-slate-700 block mb-1">
                Hiện Tượng &amp; Lý Do Chi Tiết (*):
              </label>
              <Textarea
                value={reportModal.reason}
                onChange={(e) =>
                  setReportModal((prev) => ({ ...prev, reason: e.target.value }))
                }
                placeholder="Mô tả cụ thể hiện tượng: ví dụ máy in bị kẹt giấy cụm vào, phát ra tiếng kêu lạ ở cụm 2, mã lỗi E-401..."
                className="text-xs border-slate-300 focus-visible:ring-rose-500"
                rows={3}
              />
            </div>

            <div>
              <label className="font-bold text-slate-700 block mb-1">Ghi Chú Bổ Sung:</label>
              <Input
                value={reportModal.notes}
                onChange={(e) =>
                  setReportModal((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Vị trí phát hiện, người đứng máy ca này..."
                className="text-xs h-9 border-slate-300"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setReportModal({
                  isOpen: false,
                  machineId: '',
                  issueType: 'Cơ khí',
                  reason: '',
                  severity: 'CRITICAL',
                  notes: '',
                })
              }
              className="text-xs"
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={reportIncidentMutation.isPending || !reportModal.machineId || !reportModal.reason.trim()}
              onClick={() => {
                reportIncidentMutation.mutate({
                  machineId: reportModal.machineId,
                  issueType: reportModal.issueType,
                  reason: reportModal.reason.trim(),
                  severity: reportModal.severity,
                  notes: reportModal.notes,
                });
              }}
              className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold flex items-center gap-1.5"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {reportIncidentMutation.isPending ? 'Đang gửi...' : 'Xác Nhận Báo Sự Cố'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

